import React from 'react'
import {
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Typography
} from '@mui/material'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import { Zone } from '../../../interfaces'
import { useApi } from '../../../hooks/useApi'
import { createZone, updateZone } from '../config/api'
import { zonaValidationSchema } from '../config/zonaConfig'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import { WarehouseDDL } from '../../../components/Almacen'

interface CreateEditZoneModalProps {
  zone?: Zone
  isEditing?: boolean
  closeModal: () => void
  onSuccess: () => void
  handleSnackbar: (message: string, success: boolean) => void
  onRequestStatusChange: (pendingZone: Zone) => void
}

const initialValues: Zone = {
  id: 0,
  zoneId: 0,
  warehouse: {
    warehouseId: 0,
    warehouseName: ''
  },
  zoneCode: '',
  zoneDescription: '',
  zoneName: '',
  active: true,
  warehouseId: -1,
  externalStorage: false
}

export const CreateEditZoneModal: React.FC<CreateEditZoneModalProps> = ({
  zone,
  isEditing = false,
  closeModal,
  onSuccess,
  handleSnackbar,
  onRequestStatusChange
}) => {
  const { callApi, state } = useApi()

  const handleSubmit = (
    values: Zone,
    { setSubmitting }: FormikHelpers<Zone>
  ) => {
    if (isEditing && zone && values.active !== zone.active) {
      setSubmitting(false)
      onRequestStatusChange(values)
      return
    }

    const apiCall = isEditing
      ? updateZone(values.zoneId, values)
      : createZone(values)

    callApi(apiCall)
      .then(() => {
        const message = isEditing
          ? 'Zona actualizada correctamente'
          : 'Zona creada correctamente'
        handleSnackbar(message, true)
        onSuccess()
        closeModal()
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
      .finally(() => setSubmitting(false))
  }

  if (state.status === Status.Loading) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  return (
    <Formik
      initialValues={zone || initialValues}
      validationSchema={zonaValidationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isValid,
        dirty,
        isSubmitting
      }) => (
        <Form autoComplete='off'>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                name='zoneName'
                label='Nombre Zona'
                fullWidth
                variant='outlined'
                value={values.zoneName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.zoneName && Boolean(errors.zoneName)}
                helperText={touched.zoneName && errors.zoneName}
                disabled={isSubmitting}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <WarehouseDDL
                value={values.warehouseId}
                onChange={handleChange}
                type='outlined'
                fullWidth
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={12}>
              <Field
                as={TextField}
                name='zoneDescription'
                label='Descripción Zona'
                fullWidth
                variant='outlined'
                multiline
                rows={3}
                value={values.zoneDescription}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.zoneDescription && Boolean(errors.zoneDescription)
                }
                helperText={touched.zoneDescription && errors.zoneDescription}
                disabled={isSubmitting}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Field
                    as={Checkbox}
                    name='active'
                    checked={values.active}
                    onChange={handleChange}
                    color='primary'
                    disabled={isSubmitting}
                  />
                }
                label='Activo'
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Field
                    as={Checkbox}
                    name='externalStorage'
                    checked={values.externalStorage}
                    onChange={handleChange}
                    color='primary'
                    disabled={isSubmitting}
                  />
                }
                label='Externa'
              />
            </Grid>

            <Grid item xs={12} display='flex' gap={2} justifyContent='flex-end'>
              {closeModal && (
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={closeModal}
                  disabled={isSubmitting}
                  sx={{ borderRadius: '12px' }}
                >
                  Cerrar
                </Button>
              )}
              <Button
                type='submit'
                variant='contained'
                color='primary'
                disabled={!isValid || !dirty || isSubmitting}
                sx={{ borderRadius: '12px' }}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} color='inherit' />
                  ) : undefined
                }
              >
                {isSubmitting ? 'Procesando...' : 'Guardar'}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
